import { useState, useEffect } from "react";
import { apiClient } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  FileBarChart,
  ExternalLink,
  Clock,
  CheckCircle2,
  AlertCircle,
  Loader2,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  FolderOpen
} from "lucide-react";

const statusLabels = {
  pending: "Pendiente",
  scraping: "Scrapeando",
  analyzing: "Analizando",
  completed: "Completado",
  failed: "Fallido"
};

const statusColors = {
  pending: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20",
  scraping: "bg-indigo-500/10 text-indigo-400 border-indigo-500/20",
  analyzing: "bg-cyan-500/10 text-cyan-400 border-cyan-500/20",
  completed: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  failed: "bg-red-500/10 text-red-400 border-red-500/20"
};

export default function History() {
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    loadAnalyses();
    const interval = setInterval(loadAnalyses, 15000);
    return () => clearInterval(interval);
  }, []);

  const loadAnalyses = async () => {
    try {
      const response = await apiClient.get("/analyses");
      setAnalyses(response.data);
    } catch (error) {
      toast.error("Error al cargar historial");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="loading-spinner w-10 h-10" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in" data-testid="history-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight font-['Outfit']">
            Historial de Análisis
          </h1>
          <p className="text-slate-400 mt-1">
            Todos tus análisis realizados
          </p>
        </div>
        <Button
          onClick={loadAnalyses}
          variant="outline"
          className="border-white/10 text-slate-300 hover:bg-white/5"
          data-testid="refresh-history-btn"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Actualizar
        </Button>
      </div>

      {/* Analyses List */}
      {analyses.length === 0 ? (
        <div className="glass rounded-xl p-12">
          <div className="empty-state">
            <FileBarChart className="empty-state-icon" />
            <p className="empty-state-title">Sin análisis todavía</p>
            <p className="empty-state-description">
              Lanza tu primer análisis desde el Dashboard
            </p>
            <Button
              onClick={() => window.location.href = "/"}
              className="mt-6 bg-cyan-600 hover:bg-cyan-500 text-white btn-glow"
              data-testid="go-to-dashboard-btn"
            >
              Ir al Dashboard
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {analyses.map((analysis) => (
            <AnalysisCard
              key={analysis.id}
              analysis={analysis}
              expanded={expandedId === analysis.id}
              onToggle={() => setExpandedId(expandedId === analysis.id ? null : analysis.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function AnalysisCard({ analysis, expanded, onToggle }) {
  const createdDate = new Date(analysis.created_at).toLocaleDateString("es-ES", {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });

  const completedDate = analysis.completed_at 
    ? new Date(analysis.completed_at).toLocaleDateString("es-ES", {
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit"
      })
    : null;

  const getStatusIconComponent = (status) => {
    if (status === "completed") return CheckCircle2;
    if (status === "failed") return AlertCircle;
    return Loader2;
  };
  const StatusIcon = getStatusIconComponent(analysis.status);

  return (
    <div 
      className={`glass rounded-xl overflow-hidden transition-all ${expanded ? "ring-1 ring-cyan-500/30" : ""}`}
      data-testid={`analysis-card-${analysis.id}`}
    >
      {/* Header */}
      <div 
        className="flex items-center gap-4 p-6 cursor-pointer hover:bg-white/[0.02] transition-colors"
        onClick={onToggle}
      >
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${statusColors[analysis.status]}`}>
          <StatusIcon 
            className={`w-5 h-5 ${analysis.status === "analyzing" || analysis.status === "scraping" ? "animate-spin" : ""}`} 
          />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <span className={`px-3 py-1 rounded-full text-xs font-medium border ${statusColors[analysis.status]}`}>
              {statusLabels[analysis.status]}
            </span>
            <span className="text-sm text-slate-400">
              {analysis.competitors_ids?.length || 0} competidores
            </span>
          </div>
          <div className="flex items-center gap-2 mt-2 text-sm text-slate-500">
            <Clock className="w-4 h-4" />
            <span>{createdDate}</span>
          </div>
        </div>

        <div className="flex items-center gap-4">
          {analysis.status === "completed" && (
            <div className="hidden md:flex items-center gap-6 text-sm">
              {analysis.total_ads_meta > 0 && (
                <div className="text-center">
                  <div className="text-lg font-semibold text-white">{analysis.total_ads_meta}</div>
                  <div className="text-xs text-slate-500">Meta</div>
                </div>
              )}
              {analysis.total_ads_linkedin > 0 && (
                <div className="text-center">
                  <div className="text-lg font-semibold text-white">{analysis.total_ads_linkedin}</div>
                  <div className="text-xs text-slate-500">LinkedIn</div>
                </div>
              )}
              {analysis.total_ads_google > 0 && (
                <div className="text-center">
                  <div className="text-lg font-semibold text-white">{analysis.total_ads_google}</div>
                  <div className="text-xs text-slate-500">Google</div>
                </div>
              )}
            </div>
          )}
          {expanded ? (
            <ChevronUp className="w-5 h-5 text-slate-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-slate-400" />
          )}
        </div>
      </div>

      {/* Expanded Content */}
      {expanded && (
        <div className="px-6 pb-6 border-t border-white/5 pt-4 animate-fade-in">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Meta */}
            {analysis.total_ads_meta > 0 && (
              <div className="bg-slate-950/50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
                  <span className="w-6 h-6 rounded bg-blue-500/10 text-blue-400 flex items-center justify-center text-xs font-bold">M</span>
                  Meta Ads
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Total</span>
                    <span className="text-white">{analysis.total_ads_meta}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Ganadores</span>
                    <span className="text-emerald-400">{analysis.winning_ads_meta}</span>
                  </div>
                </div>
              </div>
            )}
            {/* LinkedIn */}
            {analysis.total_ads_linkedin > 0 && (
              <div className="bg-slate-950/50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
                  <span className="w-6 h-6 rounded bg-sky-500/10 text-sky-400 flex items-center justify-center text-xs font-bold">L</span>
                  LinkedIn Ads
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Total</span>
                    <span className="text-white">{analysis.total_ads_linkedin}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Ganadores</span>
                    <span className="text-emerald-400">{analysis.winning_ads_linkedin}</span>
                  </div>
                </div>
              </div>
            )}
            {/* Google Ads */}
            {analysis.total_ads_google > 0 && (
              <div className="bg-slate-950/50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
                  <span className="w-6 h-6 rounded bg-yellow-500/10 text-yellow-400 flex items-center justify-center text-xs font-bold">G</span>
                  Google Ads
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Total</span>
                    <span className="text-white">{analysis.total_ads_google}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Ganadores</span>
                    <span className="text-emerald-400">{analysis.winning_ads_google}</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Google Drive resources: consolidated report + competitor folders */}
          {(analysis.report_url || analysis.drive_folder_url || (analysis.competitor_folder_urls && Object.keys(analysis.competitor_folder_urls).length > 0)) && (
            <div className="mt-4 bg-slate-950/50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-white mb-3">
                {analysis.drive_folder_url ? (
                  <a
                    href={analysis.drive_folder_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-white hover:text-cyan-300 transition-colors"
                    data-testid={`drive-root-link-${analysis.id}`}
                    title="Abrir carpeta raíz 'adsFunnel' en Google Drive"
                  >
                    <FolderOpen className="w-4 h-4 text-cyan-400" />
                    Recursos en Google Drive
                    <ExternalLink className="w-3 h-3 text-slate-500" />
                  </a>
                ) : (
                  <span className="inline-flex items-center gap-2">
                    <FolderOpen className="w-4 h-4 text-cyan-400" />
                    Recursos en Google Drive
                  </span>
                )}
              </h4>
              <div className="space-y-2">
                {analysis.report_url && (
                  <a
                    href={analysis.report_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-cyan-400 hover:text-cyan-300 transition-colors text-sm"
                    data-testid={`report-doc-${analysis.id}`}
                  >
                    <ExternalLink className="w-4 h-4" />
                    Ver informe consolidado en Google Docs
                  </a>
                )}
                {analysis.competitor_folder_urls && Object.keys(analysis.competitor_folder_urls).length > 0 && (
                  <div className="flex flex-wrap gap-2 pt-2">
                    {Object.entries(analysis.competitor_folder_urls).map(([name, url]) => (
                      <a
                        key={name}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/[0.03] border border-white/5 text-sm text-cyan-400 hover:text-cyan-300 hover:bg-white/[0.06] transition-colors"
                        data-testid={`folder-link-${name}`}
                        title={`Abrir carpeta de ${name} (incluye Meta, LinkedIn y Google Ads)`}
                      >
                        <FolderOpen className="w-3.5 h-3.5" />
                        Carpeta de {name}
                        <ExternalLink className="w-3 h-3 text-slate-500" />
                      </a>
                    ))}
                  </div>
                )}
                <p className="text-xs text-slate-500 pt-1">
                  Cada carpeta de competidor contiene las subcarpetas Meta, LinkedIn y Google Ads con videos, imágenes y transcripciones.
                </p>
              </div>
            </div>
          )}

          {/* Error Message */}
          {analysis.status === "failed" && analysis.error_message && (
            <div className="mt-4 p-4 bg-red-950/30 border border-red-500/20 rounded-lg">
              <p className="text-sm text-red-400">
                <strong>Error:</strong> {analysis.error_message}
              </p>
            </div>
          )}

          {/* Completion Time */}
          {completedDate && (
            <p className="text-xs text-slate-500 mt-4">
              Completado: {completedDate}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
